import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ParticleBackground } from "@/components/ui/magicui/particle-background";
import { Textarea } from "@/components/ui/textarea";
import { useTheme } from "@/contexts/ThemeProvider";
import { cn } from "@/lib/utils";
import emailjs from "@emailjs/browser";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { Check, Loader2, Mail, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

// Form schema
const formSchema = z.object({
  name: z.string().min(2, { message: "Name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  category: z.string().optional(),
  message: z
    .string()
    .min(10, { message: "Message should be at least 10 characters" }),
});

export function SupportPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Use useEffect to handle mounting
  useEffect(() => {
    setMounted(true);
  }, []);

  // Use resolvedTheme to correctly determine if dark mode is active
  const isDark = mounted ? resolvedTheme === "dark" : false;

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      category: "General Inquiry",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      setIsSubmitSuccessful(false);

      // Prepare the template parameters for EmailJS
      const templateParams = {
        name: data.name,
        email: data.email,
        category: data.category || "General Inquiry",
        message: data.message,
        title: `Support Request from ${data.name}`,
        time: new Date().toLocaleString(),
      };

      // Replace these with your actual EmailJS credentials
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
      const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

      // Send the email using EmailJS
      const response = await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      setIsSubmitSuccessful(true);

      // Close the dialog after a brief delay to show success state
      setTimeout(() => {
        setDialogOpen(false);
        setIsSubmitting(false);
        form.reset();
      }, 2000);
    } catch (error) {
      console.error("Failed to send email:", error);
      setIsSubmitting(false);
    }
  };

  const supportCategories = [
    {
      title: "Documentation",
      description: "Browse our documentation for answers to common questions.",
      link: "/docs",
      buttonText: "Browse Documentation",
      variant: "default",
    },
    {
      title: "Email Support",
      description: "Still need help? Contact our support team directly.",
      buttonText: "Contact Support",
      icon: <Mail className="h-5 w-5" />,
      variant: "outline",
      action: () => setDialogOpen(true),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Support & Help Center | Artifact - Get AI Assistant Help</title>
        <meta
          name="description"
          content="Get help with Artifact AI assistant. Contact our support team, browse documentation, or find answers to frequently asked questions. We're here to help you succeed with AI."
        />

        {/* Keywords */}
        <meta
          name="keywords"
          content="Artifact support, AI assistant help, customer service, technical support, contact support, AI help desk, troubleshooting, user guide"
        />

        {/* Canonical URL */}
        <link rel="canonical" href="https://artifact.chat/support" />

        {/* Open Graph Meta Tags */}
        <meta
          property="og:title"
          content="Support & Help Center | Artifact - Get AI Assistant Help"
        />
        <meta
          property="og:description"
          content="Get help with Artifact AI assistant. Contact our support team, browse documentation, or find answers to frequently asked questions. We're here to help you succeed with AI."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://artifact.chat/support" />
        <meta
          property="og:image"
          content="https://artifact.chat/assets/og-image.jpeg"
        />
        <meta property="og:site_name" content="Artifact" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Support & Help Center | Artifact - Get AI Assistant Help"
        />
        <meta
          name="twitter:description"
          content="Get help with Artifact AI assistant. Contact our support team, browse documentation, or find answers to frequently asked questions. We're here to help you succeed with AI."
        />
        <meta
          name="twitter:image"
          content="https://artifact.chat/assets/og-image.jpeg"
        />
        <meta name="twitter:site" content="@ArtifactAI" />

        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow" />

        {/* Structured Data for Customer Service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Artifact Support & Help Center",
            description:
              "Get help with Artifact AI assistant. Contact our support team, browse documentation, or find answers to frequently asked questions.",
            url: "https://artifact.chat/support",
            mainEntity: {
              "@type": "Organization",
              name: "Artifact",
              url: "https://artifact.chat",
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "Customer Support",
                  availableLanguage: "English",
                  areaServed: "Worldwide",
                },
              ],
            },
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero Section with Purple Gradient */}
        <section className="relative py-16 sm:py-24 overflow-hidden">
          {/* Enhanced Background Gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-purple-950/20 dark:via-purple-950/20 via-purple-200/30 to-background pointer-events-none" />

          {/* Interactive Particle Background */}
          <div className="absolute inset-0 overflow-hidden">
            <ParticleBackground
              quantity={isDark ? 40 : 30}
              speed={0.3}
              color={isDark ? "#a855f7" : "#9333ea"}
              connectThreshold={150}
              minSize={1}
              maxSize={2}
            />
          </div>

          {/* Floating Gradient Orbs */}
          <motion.div
            className="absolute top-1/3 right-1/4 w-56 h-56 rounded-full bg-gradient-to-r from-purple-600/20 dark:from-purple-600/20 from-purple-500/30 to-blue-500/20 dark:to-blue-500/20 to-blue-400/30 blur-3xl"
            animate={{
              x: [0, 20, 0],
              y: [0, -20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          <motion.div
            className="absolute bottom-1/4 left-1/4 w-64 h-64 rounded-full bg-gradient-to-r from-violet-600/20 dark:from-violet-600/20 from-violet-500/30 to-fuchsia-500/20 dark:to-fuchsia-500/20 to-fuchsia-400/30 blur-3xl"
            animate={{
              x: [0, -30, 0],
              y: [0, 30, 0],
              scale: [1, 1.15, 1],
            }}
            transition={{
              duration: 18,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2,
            }}
          />

          {/* Hero Content */}
          <div className="container relative z-10 mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center">
              <motion.h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-violet-500 to-purple-600"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
              >
                How Can We Help?
              </motion.h1>
              <motion.p
                className="text-xl text-muted-foreground mb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                We&apos;re here to help make your experience with Artifact as
                smooth as possible. Choose from the options below to get the
                support you need.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Support Options Section */}
        <section className="relative z-10 py-12 -mt-20">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
              {supportCategories.map((category, index) => (
                <motion.div
                  key={category.title}
                  className="bg-card/70 backdrop-blur-sm rounded-xl shadow-lg border border-border hover:border-purple-500/30 transition-all duration-300"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <div className="p-8">
                    <h3 className="text-2xl font-semibold mb-3 text-foreground">
                      {category.title}
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      {category.description}
                    </p>
                    {category.link ? (
                      <Link to={category.link}>
                        <Button
                          variant={category.variant}
                          className={cn(
                            "flex items-center gap-2 w-full justify-center",
                            category.variant === "default"
                              ? "bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40"
                              : "border-purple-500/50 text-purple-500 dark:text-purple-400 hover:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-950/30 dark:hover:bg-purple-950/30 hover:bg-purple-100/50"
                          )}
                        >
                          {category.buttonText} {category.icon}
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        variant={category.variant}
                        onClick={category.action}
                        className={cn(
                          "flex items-center gap-2 w-full justify-center",
                          category.variant === "default"
                            ? "bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40"
                            : "border-purple-500/50 text-purple-500 dark:text-purple-400 hover:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-950/30 dark:hover:bg-purple-950/30 hover:bg-purple-100/50"
                        )}
                      >
                        {category.icon} {category.buttonText}
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Hours Section */}

        {/* Contact Dialog */}
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            // Reset form state when dialog is closed
            if (!open) {
              setIsSubmitting(false);
              setIsSubmitSuccessful(false);
            }
            setDialogOpen(open);
          }}
        >
          <DialogContent
            className="sm:max-w-[500px] w-[calc(100%-2rem)] mx-auto"
            hideCloseButton={true}
          >
            {/* Custom close button */}
            <DialogClose asChild>
              <button
                className="absolute right-4 top-4 p-1 rounded-md border border-red-500 bg-transparent hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors z-[60]"
                aria-label="Close"
              >
                <X className="h-4 w-4 text-red-500" />
              </button>
            </DialogClose>

            <DialogHeader>
              <DialogTitle>Contact Support</DialogTitle>
              <DialogDescription>
                Fill out the form below to send a message to our support team.
              </DialogDescription>
            </DialogHeader>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Your name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="your-email@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <FormControl>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          {...field}
                        >
                          <option value="General Inquiry">
                            General Inquiry
                          </option>
                          <option value="Technical Support">
                            Technical Support
                          </option>
                          <option value="Billing">Billing</option>
                          <option value="Feature Request">
                            Feature Request
                          </option>
                          <option value="Bug Report">Bug Report</option>
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Please describe your issue or question"
                          {...field}
                          rows={4}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button
                    type="submit"
                    className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        {isSubmitSuccessful ? (
                          <>
                            <Check className="mr-2 h-4 w-4 animate-fade-in" />{" "}
                            Sent!
                          </>
                        ) : (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                            Sending...
                          </>
                        )}
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}

export default SupportPage;
